import { Injectable } from '@angular/core';
import { Observable, throwError, Subject } from 'rxjs';
import { catchError, flatMap, map } from 'rxjs/operators';
import { SohoModalDialogService } from 'ids-enterprise-ng';
import * as urltemplate from 'url-template';

import { FormResource } from '@resources/form.resource';
import { LMDialogButtonKey, LMUniqueIdService } from '@utils/index';
import { LMErrorUtilService } from '@services/utils';
import { LMDataService, LMSpecService, LMUrlUtilService } from '@services/index';
import { LMActionFormDialogComponent } from '@modal/lm-action-form-dialog.component';
import { LMActionFormDialogResultCode } from '@modal/lm-action-form-dialog.service';

@Injectable()
export class LMScheduleDialogService {
  constructor(
    private sohoModalDialogService: SohoModalDialogService,
    private lmSpecService: LMSpecService,
    private lmDataService: LMDataService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmUniqueIdService: LMUniqueIdService
  ) {}

  // Form used for Scheduling
  readonly formResource = new FormResource({formName: 'CreateRequestBasicForm', busClassName: 'AsyncActionRequest'});

  private getScheduleFormInfo(busClassName: string, actionName: string): Observable<LMScheduleFormInfo> {
    const params = {
      output: 'csvpdfschedule',
      action: 'CreateRequest'
    } as LMUrlParams;
    // This is set when it's not for a pdf or csv dialog
    if (busClassName && actionName) {
      params.scheduleAction = `${busClassName}.${actionName}`;
    }

    const resource = this.formResource;
    const url = this.lmUrlUtilService.buildUrl({ resource, params });
    return this.lmSpecService.getFormSpecFromDialogActionUrl(url).pipe(
      flatMap((formSpecResponse: LMFormSpecResponse) => {
        const formUrlInfo = formSpecResponse.formUrlInfo;
        const formSpec = formSpecResponse.formSpec;
        // TODO: This urltemplate boilerplate is everywhere, it'd be nice to have a simpler method in lmUrlUtilService
        const dataUrlTemplate = urltemplate.parse(formUrlInfo.initialDataUrl);
        let dataUrl = dataUrlTemplate.expand({ resourceId: resource.dataResourceId, actionName: 'CreateRequest' });
        dataUrl = this.lmUrlUtilService.updateUrlParams(dataUrl, params);

        return this.lmDataService.getRecord(dataUrl).pipe(
          map((scheduleView: LMDataView) => {
            return {
              scheduleView,
              formUrlInfo,
              formSpec
            } as LMScheduleFormInfo;
          })
        );
      })
    );
  }

  private showActionFormDialog(info: LMScheduleFormInfo, actionDialog$?: Subject<any>): Observable<LMDataView> {
    const automationId = `lm-${info.formSpec?.automationId}-modal`;
    const attributes: SohoAttributesArray = [ {
      name: 'data-automation-id',
      value: automationId
    }];
    const sohoModalOptions: SohoModalOptions = {
      //id:         automationId, //Note: setting id here breaks QA tests
      title:    info.formSpec.busFormSpec.title.resourceValue,
      cssClass: 'lm-themed-modal',
      fullsize: 'responsive',
      attributes
    };
    const dialog = this.sohoModalDialogService.modal(LMActionFormDialogComponent, null, sohoModalOptions);
    const dialogId = 'ScheduleDialog';

    const actionForm = dialog.componentDialog;
    actionForm.formUrlInfo = info.formUrlInfo;
    actionForm.formSpec = info.formSpec;
    actionForm.dataView = info.scheduleView;

    const actionSpec = info.formSpec.busClassSpec.actionSpec;
    const actionFormDialogData = { actionSpec };
    actionForm.formOptions = { actionFormDialogData };
    actionForm.triggerDetectChanges();
    const cancelButton: SohoModalButton = {
      id: this.lmUniqueIdService.uniqueId([dialogId, LMDialogButtonKey.CANCEL]),
      text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
      click: () => dialog.close()
    };
    const submitButton: SohoModalButton = {
      id: this.lmUniqueIdService.uniqueId([dialogId, LMDialogButtonKey.OK]),
      isDefault: true,
      text: Soho.Locale.translate(LMDialogButtonKey.OK),
      click: () => dialog.close(actionForm.formComponent.getDataView())
    };
    actionForm.defaultButton = submitButton;

    dialog.buttons([cancelButton, submitButton]).open();

    // Return afterClose as LMDataView observable
    return new Observable<LMDataView> (observer => {
      dialog
        .beforeOpen((e) => {
          const elementNodeList = document.querySelectorAll('.lm-themed-modal .modal-header');
          if (elementNodeList.length === 0) {
            return true;
          }
          const classList = elementNodeList[0].classList;          classList?.add('is-personalizable', 'header');
          return true;
        });
      dialog
        .afterClose((result => {
          // time out is needed here to allow the soho dialog control
          // to close, else if the process continues another Dialog such as
          // the response required dialog will be listening to the afterClose event
          // and be closed by mistake.  Use case is running a set action with no ui
          // from a menu that will open a schedule modal then followed by a response
          // required model
          setTimeout(() => {
            observer.next(result);
            observer.complete();
            if (actionDialog$) {
              actionDialog$.next({
                result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_ENABLEBUTTONS
              });
            }
          });
        }));
    });
  }

  /**
   * Opens a Schedule Form modal where you can set an scheduled action request
   * Returns an Observable where you can get a scheduleView needed for scheduled printing
   * @param                 busClassName
   * @param                 actionName
   * @param           actionDialog$
   * @return sachedule dataview
   */
  public openScheduleDialog(busClassName: string, actionName: string, actionDialog$?: Subject<any>): Observable<LMDataView> {
    // Fetch Schedule Form Spec and DataView
    return <Observable<LMDataView>>this.getScheduleFormInfo(busClassName, actionName)
      .pipe(
        // Show Action Form Dialog
        flatMap((info: LMScheduleFormInfo) => this.showActionFormDialog(info, actionDialog$)),
        catchError(error => {
          this.lmErrorUtilService.handleError(error.message);
          return throwError(error);
        })
      );
  }
}

export interface LMScheduleFormInfo {
  scheduleView: LMDataView,
  formSpec: LMFormSpec,
  formUrlInfo: LMFormUrlInfo
}
